/**
 * WorkOrderQuickCreate
 *
 * Mobile-first slide-up drawer for quickly creating a work order
 * from the Command Center Location Workspace.
 */

import { useState } from "react";
import { Drawer, Input, Select, Button, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
	createWorkOrder,
	type WorkOrderType,
	type WorkOrderPriority,
} from "@/services/work-order-service";
import "./WorkOrderQuickCreate.scss";

const { TextArea } = Input;

interface WorkOrderQuickCreateProps {
	open: boolean;
	locationId: number;
	onClose: () => void;
	onCreated: () => void;
}

const WORK_TYPES: { value: WorkOrderType; label: string }[] = [
	{ value: "EMERGENCY", label: "Emergency" },
	{ value: "CORRECTIVE", label: "Corrective" },
	{ value: "PREVENTIVE", label: "Preventive" },
	{ value: "INSPECTION", label: "Inspection" },
];

const PRIORITIES: { value: WorkOrderPriority; label: string; color: string }[] = [
	{ value: 1, label: "P1 – Emergency", color: "#ff4d4f" },
	{ value: 2, label: "P2 – Urgent", color: "#fa8c16" },
	{ value: 3, label: "P3 – High", color: "#faad14" },
	{ value: 4, label: "P4 – Medium", color: "#1890ff" },
	{ value: 5, label: "P5 – Low", color: "#8c8c8c" },
];

export const WorkOrderQuickCreate = ({
	open,
	locationId,
	onClose,
	onCreated,
}: WorkOrderQuickCreateProps) => {
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [workType, setWorkType] = useState<WorkOrderType>("CORRECTIVE");
	const [priority, setPriority] = useState<WorkOrderPriority>(3);
	const [submitting, setSubmitting] = useState(false);

	const handleSubmit = async () => {
		if (!title.trim()) {
			message.warning("Please enter a title");
			return;
		}

		setSubmitting(true);
		try {
			await createWorkOrder({
				locationId,
				title: title.trim(),
				description: description.trim() || undefined,
				workType,
				priority,
			});
			message.success("Work order created");
			// Reset form
			setTitle("");
			setDescription("");
			setWorkType("CORRECTIVE");
			setPriority(3);
			onCreated();
		} catch (error: any) {
			message.error(error.message || "Failed to create work order");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Drawer
			open={open}
			onClose={onClose}
			placement="bottom"
			height="auto"
			className="wo-quick-create-drawer"
			title="New Work Order"
			destroyOnClose
		>
			<div className="wo-qc-form">
				<div className="wo-qc-field">
					<label>Title</label>
					<Input
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						placeholder="Brief description of the issue..."
						size="large"
						maxLength={200}
					/>
				</div>

				<div className="wo-qc-row">
					<div className="wo-qc-field wo-qc-half">
						<label>Type</label>
						<Select
							value={workType}
							onChange={setWorkType}
							options={WORK_TYPES}
							size="large"
							className="wo-qc-select"
						/>
					</div>
					<div className="wo-qc-field wo-qc-half">
						<label>Priority</label>
						<Select
							value={priority}
							onChange={setPriority}
							size="large"
							className="wo-qc-select"
						>
							{PRIORITIES.map((p) => (
								<Select.Option key={p.value} value={p.value}>
									<span style={{ color: p.color, fontWeight: 600 }}>{p.label}</span>
								</Select.Option>
							))}
						</Select>
					</div>
				</div>

				<div className="wo-qc-field">
					<label>Description <span className="wo-qc-optional">(optional)</span></label>
					<TextArea
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						placeholder="Additional details, affected area, symptoms..."
						rows={3}
						maxLength={2000}
					/>
				</div>

				<Button
					type="primary"
					size="large"
					block
					icon={<PlusOutlined />}
					loading={submitting}
					onClick={handleSubmit}
					className="wo-qc-submit"
				>
					Create Work Order
				</Button>
			</div>
		</Drawer>
	);
};
